/**
 * Pricing Assessment Service
 *
 * POSTs the pricing-tier wizard payload to the droplet-hosted
 * landing-pages-api at /api/v1/hoa/pricing-assessment.
 *
 * The droplet endpoint requires snake_case keys (no fallback normalization
 * on this route), so submitAssessment expects callers to pass the body in
 * the snake_case shape the route handler destructures.
 *
 * The droplet does not yet expose a GET addon-catalog endpoint, so
 * getAddonCatalog still returns []. Pricing wizard consumers wrap that call
 * in try/catch and degrade to an empty list.
 */

const API_BASE_URL = import.meta.env.VITE_LANDING_PAGES_API_URL || 'http://localhost:3002';

export interface AddonCatalog {
  id: string;
  created_at: string;
  addon_code: string;
  addon_name: string;
  description?: string;
  price_display?: string;
  available_in_tiers: string[];
  active: boolean;
  sort_order: number;
}

export interface PricingAssessmentResult {
  success: boolean;
  assessmentId?: string;
  message: string;
  errors?: string[];
}

interface PricingApiResponse {
  success: boolean;
  message?: string;
  error?: string;
  data?: {
    id: string;
    community_name?: string;
    selected_tier?: string;
    created_at?: string;
  };
}

class PricingAssessmentService {
  /**
   * Returns the addon catalog for the given tier. The droplet does not yet
   * expose a catalog endpoint, so this always returns []. Consumers handle
   * the empty list gracefully.
   */
  async getAddonCatalog(_tierCode?: string): Promise<AddonCatalog[]> {
    return [];
  }

  async submitAssessment(assessment: Record<string, unknown>): Promise<PricingAssessmentResult> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/hoa/pricing-assessment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(assessment),
      });

      if (response.status === 429) {
        return {
          success: false,
          message:
            'Too many submissions from this network. Please wait a few minutes and try again.',
        };
      }

      const result: PricingApiResponse = await response.json().catch(() => ({
        success: false,
        error: 'Invalid response from server',
      }));

      if (!response.ok || !result.success || !result.data?.id) {
        return {
          success: false,
          message: result.message || result.error || 'Failed to submit pricing assessment.',
        };
      }

      return {
        success: true,
        assessmentId: result.data.id,
        message: result.message || 'Pricing assessment received',
      };
    } catch (error) {
      console.error('[pricingAssessmentService] submission failed:', error);
      return {
        success: false,
        message:
          'Network error submitting pricing assessment. Please check your connection and try again.',
      };
    }
  }
}

export const pricingAssessmentService = new PricingAssessmentService();
